import { clsx } from "clsx";
import { LinkProps, Link as RRLink } from "react-router-dom";

import { RVIcon, RVIconSizeOptions } from "../RVIcon/RVIcon";

import styles from "./RVLink.module.scss";

export type RVLinkProps = {
    /**
     * The destination URL or path for the link
     */
    to: LinkProps["to"];
    /**
     * The text content of the link
     */
    text: string;
    /**
     * Class name that is applied directly to this hyperlink's <a/> tag
     */
    className?: string;
    /**
     * Class name that is applied to div inside of the hyperlink's <a/> tag.
     * Often layout-related styling cannot be applied directly to a hyperlink, and
     * should instead be applied to the content it wraps
     */
    innerDivClassName?: string;
    /**
     * Show the external link icon
     */
    showIcon?: boolean;
    /**
     * Test ID for use with testing-library's getByTestId
     */
    "data-testid"?: string;
};

/**
 * RVLink
 *
 * A link component that uses React Router for internal navigation.
 * Opens in a new tab by default with external link icon.
 *
 * @param {RVLinkProps} props - Component props
 * @returns {JSX.Element}
 */
export function RVLink({
    to,
    text,
    className = "",
    innerDivClassName = "",
    showIcon = true,
    "data-testid": dataTestId,
}: RVLinkProps): JSX.Element {
    return (
        <RRLink
            to={to}
            className={clsx(styles.link, className)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={dataTestId}
        >
            <div className={clsx(styles.linkContent, innerDivClassName)}>
                {/* Extra spaces help simplify underline state for the icon */}
                {text}
                {showIcon && (
                    <>
                        &nbsp; &nbsp; &nbsp; &nbsp;
                        <RVIcon icon="expand" size={RVIconSizeOptions.SMALL} className={styles.icon} />
                    </>
                )}
            </div>
        </RRLink>
    );
}
